import { gql } from '@apollo/client';
import {
  AlertDescription,
  Box,
  Button,
  chakra,
  HStack,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { HOUR_REGEX } from '~utils/timeBlocks';
import { useBulkUpdateBurnInRefreshMutation } from './__generated__/BulkUpdateBurnInRefresh.graphql';
import BackToAllActionsButton from './BackToAllActionsButton';
import { BulkActionComponent } from './BulkActionModal';

const schema = z.object({
  displayIds: z.array(z.string()),
  time: z.string(),
});
type FormValues = z.TypeOf<typeof schema>;

export const BulkUpdateBurnInRefresh: BulkActionComponent = ({
  displays,
  onCancel,
  onBack,
  onSuccess,
}) => {
  const [bulkUpdateBurnInRefresh] = useBulkUpdateBurnInRefreshMutation();
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      displayIds: displays
        .filter(({ burnInRefreshSettings }) => burnInRefreshSettings != null)
        .map((display) => display.id),
      time: '02:00',
    },
    resolver: zodResolver(schema),
  });
  const toast = useToast();
  const analytics = useAnalyticsReporter();

  const burnInRefreshNotSupported = displays.filter((d) => !d.burnInRefreshSettings);
  const burnInRefreshNotSupportedCount = burnInRefreshNotSupported.length;
  const burnInRefreshNotSupportedAlias =
    burnInRefreshNotSupportedCount === 1 ? burnInRefreshNotSupported[0].alias : null;

  const handleBulkUpdateBurnInRefresh = async ({ displayIds, time }: FormValues) => {
    try {
      if (displayIds.length > 0 && time.length === 5) {
        await bulkUpdateBurnInRefresh({
          variables: {
            input: {
              displayIds,
              time,
            },
          },
        });

        if (displayIds.length === 1) {
          analytics.track('burnInRefreshUpdate');
        } else {
          analytics.track('displayBulkActionComplete', {
            action: 'updateBurnInRefresh',
            displayCount: displayIds.length,
          });
        }
      }

      await onSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: t('bulkBurnInRefreshErr'),
        description: fromError(err, 'BulkUpdateBurnInRefresh'),
      });
    }
  };

  return (
    <>
      <ModalContent>
        <ModalHeader>{t('burnInRefresh')}</ModalHeader>
        <ModalCloseButton onClick={onCancel} isDisabled={isSubmitting} />
        <form onSubmit={handleSubmit(handleBulkUpdateBurnInRefresh)}>
          {burnInRefreshNotSupportedCount > 0 && (
            <Box px="7" py="8">
              <WarningAlert>
                <AlertDescription>
                  <Trans
                    i18nKey="burnInRefreshNotSupported"
                    count={burnInRefreshNotSupportedCount}
                    values={{
                      name: burnInRefreshNotSupportedAlias,
                      count: burnInRefreshNotSupportedCount,
                    }}
                    components={{
                      strong: <chakra.strong />,
                    }}
                  />
                </AlertDescription>
              </WarningAlert>
            </Box>
          )}
          {burnInRefreshNotSupportedCount === 0 && (
            <ModalBody>
              <Stack spacing="4">
                <Text color="gray.500" mb="2">
                  {t('burnInRefreshText')}
                </Text>
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <TimeSelect
                      property="start"
                      value={field.value}
                      onChange={(_property, value) => {
                        if (!HOUR_REGEX.test(value)) return;
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Stack>
            </ModalBody>
          )}
          <ModalFooter>
            <HStack flex="1" alignItems="center">
              <BackToAllActionsButton onBack={onBack} isDisabled={isSubmitting} />
              <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  onClick={onCancel}
                  isDisabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button
                  marginLeft="3"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  isDisabled={isSubmitting || burnInRefreshNotSupportedCount > 0}
                  isLoading={isSubmitting}
                >
                  {t('apply')}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </>
  );
};

BulkUpdateBurnInRefresh.graphql = {
  fragments: {
    BulkUpdateBurnInRefresh_display: gql`
      fragment BulkUpdateBurnInRefresh_display on Display {
        burnInRefreshSettings {
          reported {
            time
          }
          desired {
            time
          }
        }
      }
    `,
  },
  mutations: {
    BulkUpdateBurnInRefresh: gql`
      mutation BulkUpdateBurnInRefresh($input: DisplayBulkUpdateBurnInRefreshInput!) {
        displayBulkUpdateBurnInRefresh(input: $input) {
          displays {
            id
            burnInRefreshSettings {
              reported {
                time
              }
              desired {
                time
              }
            }
          }
        }
      }
    `,
  },
};
